import React, {FC, useCallback} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {DataEventActionsEnum} from '../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {TopBarDataEventObject} from '../../../../../libs/shared/core/src/models/tracking/objectNames/topBarDataEventObject';
import {useDataEventTracking} from '../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {AppPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {useLinkNavigation} from '../../../../../libs/shared/ui/src/components/navigation/useLinkNavigation';
import {useAppDispatch, useAppSelector} from '../../../../helpers/appReduxHelpers';
import {LmsCardLocationsEnum, trackLmsWithLocation} from '../../../../settings/lms/lmsHelpers';
import {
  getHasIndividualTrainingAccess,
  getHasWebinarTrainingAccess,
} from '../../../../store/data/company/companySelectors';
import {navigateTopBarPopoverAction} from '../../../../store/panels/app/appPanelActions';
import {AppPanelPopoverTypesEnum} from '../../../../store/panels/app/appPanelState';
import {lmsFrontAcademyUrl, webinarTrainingUrl} from '../../../../store/stateless/navigationActions';
import {HelpTopicCard} from '../../../help/helpTopicCard';
import {
  individualTrainingCardContent,
  lmsCardContent,
  webinarTrainingCardContent,
} from '../../../help/helpTopicCardContent';
import {useIndividualTrainingChiliPiperDialog} from '../../../onboarding/chiliPiper/individualTrainingChiliPiperDialog';

/*
 * Props.
 */

interface SetupGuideHelpFooterProps {
  panelId: AppPanelId;
  className?: string;
  onRequestClose: () => void;
}

/*
 * Style.
 */

const StyledResourcesSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyledHeaderLine = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
`;

const StyledResourceTitle = styled.h3`
  margin: 0;
  color: ${(p) => p.theme.greys.shade90};
`;

const StyledResourcesContainerDiv = styled.div`
  display: flex;
  gap: 16px;
`;

const StyledHelpCard = styled(HelpTopicCard)`
  flex: 1 1 50%;
`;

/*
 * Component.
 */

export const SetupGuideHelpFooter: FC<SetupGuideHelpFooterProps> = (props) => {
  const {className, panelId, onRequestClose} = props;
  const {navigateToLink} = useLinkNavigation();
  const {trackDataEvent} = useDataEventTracking();
  const dispatch = useAppDispatch();
  const hasIndividualTrainingAccess = useAppSelector(getHasIndividualTrainingAccess);
  const hasWebinarTrainingAccess = useAppSelector(getHasWebinarTrainingAccess);
  const onOpenHelpClick = () => {
    onRequestClose();
    dispatch(navigateTopBarPopoverAction(panelId, AppPanelPopoverTypesEnum.HELP));
  };

  const renderWebinarTrainingAccessCards = useCallback(
    () => (
      <StyledResourcesContainerDiv>
        <StyledHelpCard
          content={webinarTrainingCardContent}
          onClick={() => {
            navigateToLink(webinarTrainingUrl);
            trackDataEvent(DataEventActionsEnum.CLICK, TopBarDataEventObject.LIVE_WEBINAR);
          }}
        />
        <StyledHelpCard
          content={lmsCardContent}
          onClick={() => {
            navigateToLink(lmsFrontAcademyUrl);
            trackLmsWithLocation(LmsCardLocationsEnum.P0_ONBOARDING_CHECKLIST);
          }}
        />
      </StyledResourcesContainerDiv>
    ),
    [navigateToLink, trackDataEvent],
  );

  const [individualTrainingDialog, onIndividualTrainingClick] = useIndividualTrainingChiliPiperDialog(
    TopBarDataEventObject.BOOK_A_SETUP_CALL,
  );

  const renderIndividualTrainingAccessCards = useCallback(
    () => (
      <StyledResourcesContainerDiv>
        <StyledHelpCard content={individualTrainingCardContent} onClick={onIndividualTrainingClick} />
        <StyledHelpCard
          content={lmsCardContent}
          onClick={() => {
            navigateToLink(lmsFrontAcademyUrl);
            trackLmsWithLocation(LmsCardLocationsEnum.P0_ONBOARDING_CHECKLIST);
          }}
        />
      </StyledResourcesContainerDiv>
    ),
    [navigateToLink, onIndividualTrainingClick],
  );

  const shouldShowWebinarTraining = hasWebinarTrainingAccess && !hasIndividualTrainingAccess;
  const shouldShowFallback = !hasWebinarTrainingAccess && !hasIndividualTrainingAccess;

  return (
    <>
      <StyledResourcesSection className={className}>
        <StyledHeaderLine>
          <StyledResourceTitle>
            <Trans id="onboarding-widget-help-footer-title">Need some help?</Trans>
          </StyledResourceTitle>

          <Button variant={ButtonVariantsEnum.LINK} isRounded onClick={onOpenHelpClick}>
            <Trans id="onboarding-widget-help-footer-cta">View more resources</Trans>
          </Button>
        </StyledHeaderLine>

        {hasIndividualTrainingAccess && renderIndividualTrainingAccessCards()}
        {shouldShowWebinarTraining && renderWebinarTrainingAccessCards()}
        {shouldShowFallback && (
          <StyledResourcesContainerDiv>
            <StyledHelpCard
              content={lmsCardContent}
              onClick={() => {
                navigateToLink(lmsFrontAcademyUrl);
                trackLmsWithLocation(LmsCardLocationsEnum.P0_ONBOARDING_CHECKLIST);
              }}
            />
          </StyledResourcesContainerDiv>
        )}
      </StyledResourcesSection>
      {individualTrainingDialog}
    </>
  );
};
